import { Component, Prop, h, State, Element } from '@stencil/core';

/**
 * YouTube video component with lazy loading and preview functionality
 * @example
 *   <me-video-youtube video-id="JiaYUmPz3TI" video-title="Example video title" lazy="true"></me-video-youtube>
 */
@Component({
  tag: 'me-video-youtube',
  styleUrl: 'index.scss',
  shadow: false,
})
export class MeVideoYoutube {
  @Prop() videoId!: string;
  @Prop() videoTitle!: string;
  @Prop() lazy: boolean | string = true;

  @State() loaded: boolean = false;
  @State() isInViewport: boolean = false;

  @Element() el: HTMLElement;
  private observer?: IntersectionObserver;

  /**
   * Returns true if lazy loading is enabled (handles string and boolean values)
   */
  get isLazy(): boolean {
    if (typeof this.lazy === 'string') {
      return this.lazy !== 'false';
    }
    return !!this.lazy;
  }

  componentWillLoad() {
    if (!this.isLazy) {
      this.isInViewport = true;
    }
  }

  componentDidLoad() {
    if (this.isLazy) {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            this.isInViewport = true;
            this.observer?.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      this.observer.observe(this.el);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  private handlePreviewClick = () => {
    this.loaded = true;
  };

  private handlePreviewKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handlePreviewClick();
    }
  };

  render() {
    let src = `https://www.youtube.com/embed/${this.videoId}`;
    if (this.isLazy && this.loaded) {
      src += '?autoplay=1';
    }

    if (this.isLazy && !this.loaded) {
      return (
        <div class="me-video me-video--preview" aria-live="polite">
          <img
            class="me-video__preview-img"
            src={`https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg`}
            onError={e => {
              (e.currentTarget as HTMLImageElement).src = `https://img.youtube.com/vi/${this.videoId}/hqdefault.jpg`;
            }}
            alt={this.videoTitle ? `Preview for video: ${this.videoTitle}` : 'Video preview'}
          />
          <button class="me-video__play-btn" aria-label="Play video" type="button" tabIndex={0} onClick={this.handlePreviewClick} onKeyDown={this.handlePreviewKeyDown}>
            <span class="me-video__play-icon">
              <me-icon name="components--video--play" />
            </span>
          </button>
        </div>
      );
    }

    return (
      <div class="me-video" aria-live="polite">
        <iframe
          class="me-video__iframe"
          src={src}
          title={this.videoTitle}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
    );
  }
}
